/**
 * DESIGN CHALLENGES — objective-based levels that turn the sandbox into a game.
 *
 * A `Challenge` states a brief plus a set of hard `requires` (survive a gust,
 * carry named loads, stay under a steel budget, hit a height / tower type). The
 * pure `evaluateChallenge` compiles nothing itself — it takes an already-built
 * tower (`BuiltTower`) and:
 *   1. runs the physics solver HEADLESS to find the wind speed at which the
 *      tower collapses (the same settle + smooth-ramp pattern as
 *      scripts/checkTowers.ts), and
 *   2. checks each requirement against the built tower (loads read off the
 *      structure's mounted loads, steel mass from `towerMetrics`, height/type
 *      from the tower).
 *
 * `ChallengePanel` is a self-contained dark-UI widget: it lists the challenges
 * (click to select + read the brief) and renders the last evaluation's
 * per-check pass/fail. Wiring lives in the maintainer's app (see the file
 * footer): on "Test", compile the current tower, call `evaluateChallenge`, and
 * hand the result to `panel.showResult`.
 *
 * Everything here is deterministic: the solver runs with a fixed timestep and
 * steady wind (no gustiness), so the same tower always yields the same
 * survive-wind and the same result.
 */
import { type BuiltTower, towerMetrics } from "@/game/towerSpec";
import { Solver } from "@/physics/solver";
import { estimateCost, towerEconomics } from "@/game/costEstimator";

// --- challenge model -------------------------------------------------------

/**
 * A design objective. Every field in `requires` is optional; a challenge checks
 * only the constraints it sets. `requiredLoads[].kind` is an appurtenance kind
 * ("panel" | "dish" | "omni" | "rru" | "coax") and `count` the minimum number
 * of that kind that must be mounted on the tower.
 */
export interface Challenge {
  id: string;
  name: string;
  brief: string;
  requires: {
    /** Minimum gust (m/s) the tower must survive without collapsing. */
    minSurviveWind?: number;
    /** Appurtenances that must be mounted, by kind + minimum count. */
    requiredLoads?: Array<{ kind: string; count: number }>;
    /** Maximum total member steel mass (kg). */
    maxSteelKg?: number;
    /** Maximum estimated build cost (USD) — see estimateCost. */
    maxCostUSD?: number;
    /** Required tower type ("lattice" | "guyed" | "monopole"). */
    towerType?: string;
    /** Minimum tower height (m). */
    minHeight?: number;
    /** Maximum payback period (months): build cost ÷ monthly equipment rent.
     *  Forces enough revenue-earning equipment to clear the debt fast — see
     *  towerEconomics. Requires equipment (no equipment ⇒ infinite payback ⇒ fail). */
    maxPaybackMonths?: number;
  };
}

/** One requirement's verdict, for the UI. */
export interface ChallengeCheck {
  label: string;
  ok: boolean;
  detail: string;
}

/** The result of evaluating a built tower against a challenge. */
export interface ChallengeResult {
  pass: boolean;
  /** Gust speed (m/s) at which the tower collapsed in the headless run. */
  surviveWind: number;
  checks: ChallengeCheck[];
  /** Optional 0..100 quality score (higher is better). */
  score?: number;
  /** Estimated build cost (USD) from estimateCost, when computed. */
  cost?: number;
  /** Payback period (months) from towerEconomics, when computed (Infinity = no rent). */
  paybackMonths?: number;
}

// --- the challenges --------------------------------------------------------
//
// Numbers are grounded in the wind presets (src/game/windPresets.ts) and the
// appurtenance catalog (src/game/appurtenances.ts):
//   40 m/s  = TIA-222-G US-inland design gust
//   45 m/s  = AS1170 non-cyclonic (regions A/B)
//   50 m/s  = coastal / hurricane design gust
//   66 m/s  = AS1170 cyclonic (regions C/D)
//   70 m/s  = Saffir-Simpson Category 3 (major hurricane) gust

export const CHALLENGES: Challenge[] = [
  {
    id: "starter-selfsupport",
    name: "First Steps: 30 m self-support",
    brief:
      "Every tower engineer's first job. Stand a 30 m self-supporting lattice tower that survives the TIA-222 US-inland design gust of 40 m/s. No loads, no guys — just a mast that doesn't fall over.",
    requires: {
      minSurviveWind: 40,
      towerType: "lattice",
      minHeight: 30,
    },
  },
  {
    id: "coastal-monopole-dishes",
    name: "Coastal monopole: 40 m Cat-3 backhaul",
    brief:
      "A coastal site needs a slim monopole at least 40 m tall carrying two 1.2 m microwave dishes for point-to-point backhaul. It has to ride out a Saffir-Simpson Category 3 gust (70 m/s) without coming down.",
    requires: {
      minSurviveWind: 70,
      towerType: "monopole",
      minHeight: 40,
      requiredLoads: [{ kind: "dish", count: 2 }],
    },
  },
  {
    id: "guyed-broadcast-mast",
    name: "Tall guyed broadcast mast",
    brief:
      "Broadcast wants height. Raise a guyed mast at least 90 m tall that carries an omni broadcast antenna up top and survives the AS1170 non-cyclonic design gust of 45 m/s. Guys do the heavy lifting — use them.",
    requires: {
      minSurviveWind: 45,
      towerType: "guyed",
      minHeight: 90,
      requiredLoads: [{ kind: "omni", count: 1 }],
    },
  },
  {
    id: "steel-budget-efficiency",
    name: "Lean & mean: steel budget",
    brief:
      "Materials cost money. Build a 40 m lattice tower that survives 50 m/s (coastal design gust) using no more than 4 tonnes of steel. Every kilo counts — the leaner you go while still standing, the higher your score.",
    requires: {
      minSurviveWind: 50,
      towerType: "lattice",
      minHeight: 40,
      maxSteelKg: 4000,
    },
  },
  {
    id: "carrier-loaded-site",
    name: "Loaded carrier site",
    brief:
      "A real macro site: three sector panels, three RRU radios and a 1.8 m microwave dish, all on one 45 m lattice tower that must survive the AS1170 cyclonic design gust of 66 m/s. Heavy, draggy, and it still has to stand.",
    requires: {
      minSurviveWind: 66,
      towerType: "lattice",
      minHeight: 45,
      requiredLoads: [
        { kind: "panel", count: 3 },
        { kind: "rru", count: 3 },
        { kind: "dish", count: 1 },
      ],
    },
  },
  {
    id: "extreme-survivor",
    name: "The survivor: 60 m in a Cat-3+",
    brief:
      "The brutal one. A 60 m self-supporting tower carrying two 1.2 m dishes and two sector panels that must survive an 80 m/s gust — past Category 3, into Category 4 territory. Overbuild, or watch it fold.",
    requires: {
      minSurviveWind: 80,
      towerType: "lattice",
      minHeight: 60,
      requiredLoads: [
        { kind: "dish", count: 2 },
        { kind: "panel", count: 2 },
      ],
    },
  },
  {
    id: "budget-cheapest-40m",
    name: "On a budget: cheapest 40 m",
    brief:
      "Finance signed off on $120k, not a cent more. Stand a 40 m lattice tower that still rides out the coastal 50 m/s design gust — for the lowest estimated build cost you can manage. Steel, foundations, crane time: it all counts. Come in under budget and the leaner the build, the higher your score.",
    requires: {
      minSurviveWind: 50,
      towerType: "lattice",
      minHeight: 40,
      maxCostUSD: 120_000,
    },
  },
  {
    id: "budget-three-dishes",
    name: "Backhaul on a shoestring",
    brief:
      "Carry three 1.2 m microwave dishes on a tower at least 30 m tall that survives the AS1170 non-cyclonic 45 m/s gust, and keep the whole estimated build under $250k. Every dish adds equipment cost and drag — pick a structure that carries them cheaply.",
    requires: {
      minSurviveWind: 45,
      minHeight: 30,
      requiredLoads: [{ kind: "dish", count: 3 }],
      maxCostUSD: 250_000,
    },
  },
  {
    id: "landlord",
    name: "The Landlord",
    brief:
      "Money talks. Stand a tower at least 40 m tall and load it with enough revenue-earning equipment (panels, dishes, radios) that the monthly rent pays back its build cost within 48 months — while it still survives the AS1170 non-cyclonic 45 m/s design gust. More gear means faster payback but more wind area: find the balance where the rent clears the debt and the tower stays up.",
    requires: {
      minSurviveWind: 45,
      minHeight: 40,
      maxPaybackMonths: 48,
    },
  },
  {
    id: "quick-buck",
    name: "Quick Buck",
    brief:
      "The investor wants their money back fast. Stand a tower at least 40 m tall and pay the build off in under 36 months while surviving a 55 m/s coastal gust. You'll need a dense, high-rent load-out on a structure cheap and strong enough to carry it — overload it and it folds before it earns a cent.",
    requires: {
      minSurviveWind: 55,
      minHeight: 40,
      maxPaybackMonths: 36,
    },
  },
];

// --- headless survive-wind ------------------------------------------------

const DT = 1 / 60;
const SETTLE_STEPS = 200; // ~3.3 s to settle from the built geometry
const MAX_WIND = 90; // m/s ceiling for the ramp
const RAMP_RATE = 5; // m/s per sim-second (matches scripts/checkTowers.ts)

/**
 * Run the solver headless on a tower and return the gust speed (m/s) at which
 * it collapses. Settles under gravity, then smoothly ramps the wind dial until
 * `collapsed`. Returns 0 if the tower can't even stand at zero wind, and
 * MAX_WIND if it survives the whole ramp. Deterministic (fixed dt, steady wind).
 */
export function surviveWind(built: BuiltTower): number {
  const solver = new Solver(built.structure, { windSpeed: 0 });

  for (let i = 0; i < SETTLE_STEPS; i++) solver.step(DT);
  if (solver.collapsed) return 0;

  let v = 0;
  while (v < MAX_WIND) {
    v += RAMP_RATE * DT;
    solver.params.windSpeed = v;
    solver.step(DT);
    if (solver.collapsed) return Math.round(v);
  }
  return MAX_WIND;
}

/**
 * Peak member utilization when the tower is held at its DESIGN gust `windTarget`.
 * This is the material-efficiency signal for scoring: a tower running near 1.0 at
 * its rated load is lean (well-used steel); one loafing at 0.2 is grossly
 * overbuilt. Settles, ramps to the target, then holds and takes the peak util.
 * Returns Infinity if it collapses at or below the target (shouldn't, if it passed).
 */
export function peakUtilAtWind(built: BuiltTower, windTarget: number): number {
  const solver = new Solver(built.structure, { windSpeed: 0 });
  for (let i = 0; i < SETTLE_STEPS; i++) solver.step(DT);
  if (solver.collapsed) return Infinity;
  let v = 0;
  while (v < windTarget) {
    v += RAMP_RATE * DT;
    solver.params.windSpeed = Math.min(v, windTarget);
    solver.step(DT);
    if (solver.collapsed) return Infinity;
  }
  let peak = 0;
  for (let i = 0; i < 120; i++) {
    solver.params.windSpeed = windTarget;
    solver.step(DT);
    if (solver.collapsed) return Infinity;
    peak = Math.max(peak, solver.maxUtil);
  }
  return peak;
}

// --- load introspection ----------------------------------------------------

/** Count mounted appurtenances on a built tower, grouped by kind. */
function loadCountsByKind(built: BuiltTower): Map<string, number> {
  const counts = new Map<string, number>();
  for (const node of built.structure.nodes) {
    if (!node.mountedLoads) continue;
    for (const { load } of node.mountedLoads) {
      const kind = load.appurtenance.kind;
      counts.set(kind, (counts.get(kind) ?? 0) + 1);
    }
  }
  return counts;
}

// --- evaluation ------------------------------------------------------------

/**
 * Evaluate a built tower against a challenge. Pure: it takes the compiled
 * `BuiltTower`, runs the headless survive-wind, and checks every requirement.
 * Returns per-check verdicts and an overall pass. `score` (0..100) rewards
 * engineering efficiency: high material utilization at the design gust and the
 * lowest build cost, with a small bonus for wind headroom (see the score block).
 */
export function evaluateChallenge(ch: Challenge, built: BuiltTower): ChallengeResult {
  const wind = surviveWind(built);
  const metrics = towerMetrics(built);
  const cost = estimateCost(built).total;
  const econ = towerEconomics(built);
  const checks: ChallengeCheck[] = [];

  const req = ch.requires;

  // --- survive wind ---
  if (req.minSurviveWind !== undefined) {
    const target = req.minSurviveWind;
    const ok = wind >= target;
    checks.push({
      label: "Survives wind",
      ok,
      detail:
        wind <= 0
          ? `collapsed before wind (needs ${target} m/s)`
          : `survives ${wind} m/s / needs ${target} m/s`,
    });
  }

  // --- tower type ---
  if (req.towerType !== undefined) {
    const ok = built.type === req.towerType;
    checks.push({
      label: "Tower type",
      ok,
      detail: ok
        ? `${built.type}`
        : `is ${built.type}, needs ${req.towerType}`,
    });
  }

  // --- height ---
  if (req.minHeight !== undefined) {
    const ok = built.height >= req.minHeight;
    checks.push({
      label: "Height",
      ok,
      detail: `${built.height.toFixed(1)} m / needs ≥ ${req.minHeight} m`,
    });
  }

  // --- required loads ---
  if (req.requiredLoads !== undefined && req.requiredLoads.length > 0) {
    const counts = loadCountsByKind(built);
    for (const need of req.requiredLoads) {
      const have = counts.get(need.kind) ?? 0;
      const ok = have >= need.count;
      checks.push({
        label: `Loads: ${need.kind}`,
        ok,
        detail: `${have} × ${need.kind} / needs ≥ ${need.count}`,
      });
    }
  }

  // --- steel budget ---
  if (req.maxSteelKg !== undefined) {
    const kg = metrics.steelMassKg;
    const ok = kg <= req.maxSteelKg;
    checks.push({
      label: "Steel budget",
      ok,
      detail: `${(kg / 1000).toFixed(2)} t / max ${(req.maxSteelKg / 1000).toFixed(2)} t`,
    });
  }

  // --- cost budget ---
  const fmtUSD = (usd: number) =>
    usd >= 1000 ? `$${(usd / 1000).toFixed(0)}k` : `$${Math.round(usd)}`;
  if (req.maxCostUSD !== undefined) {
    const ok = cost <= req.maxCostUSD;
    checks.push({
      label: "Cost budget",
      ok,
      detail: `Cost ${fmtUSD(cost)} ${ok ? "≤" : ">"} ${fmtUSD(req.maxCostUSD)}`,
    });
  }

  // --- payback (build cost ÷ monthly rent) ---
  if (req.maxPaybackMonths !== undefined) {
    const ok = isFinite(econ.paybackMonths) && econ.paybackMonths <= req.maxPaybackMonths;
    checks.push({
      label: "Payback",
      ok,
      detail: isFinite(econ.paybackMonths)
        ? `${econ.paybackMonths.toFixed(0)} mo @ ${fmtUSD(econ.monthlyRevenue)}/mo rent / needs ≤ ${req.maxPaybackMonths} mo`
        : `no equipment / needs ≤ ${req.maxPaybackMonths} mo`,
    });
  }

  const pass = checks.length > 0 && checks.every((c) => c.ok);

  // --- score (0..100) --------------------------------------------------------
  // Only meaningful when the tower passes. The score rewards ENGINEERING EFFICIENCY:
  // the cheapest tower that uses its material hardest (highest utilization at the
  // design gust) wins. Overbuilding a tower that loafs at 20% util, or spending far
  // more than the job needs, both cost you points. A small robustness bonus keeps a
  // sensible safety margin worthwhile. Deterministic.
  //
  //   • Utilization (50 pts): peak util at the design wind — lean, well-used steel.
  //   • Cost (40 pts): cheaper is better — vs the budget if set, else $/m of height.
  //   • Robustness (10 pts): a little wind headroom over the target.
  let score: number | undefined;
  if (pass) {
    const target = req.minSurviveWind ?? Math.max(1, Math.round(wind * 0.8));

    // Utilization at the design gust (Infinity guard → treat as 0 efficiency).
    const util = peakUtilAtWind(built, target);
    const utilScore = Number.isFinite(util) ? Math.max(0, Math.min(1, util)) : 0;

    // Cost efficiency: prefer the tightest binding budget; otherwise benchmark the
    // build against a $/metre-of-height band ($1.5k/m → full marks, $6k/m → zero).
    let costScore: number;
    if (req.maxCostUSD !== undefined) {
      costScore = 1 - Math.min(1, cost / req.maxCostUSD);
    } else if (req.maxSteelKg !== undefined) {
      costScore = 1 - Math.min(1, metrics.steelMassKg / req.maxSteelKg);
    } else if (req.maxPaybackMonths !== undefined && isFinite(econ.paybackMonths)) {
      costScore = 1 - Math.min(1, econ.paybackMonths / req.maxPaybackMonths);
    } else {
      const costPerM = cost / Math.max(1, built.height);
      costScore = Math.max(0, Math.min(1, (6000 - costPerM) / (6000 - 1500)));
    }

    // Robustness: up to +10 pts for surviving ~50% past the design gust.
    const headroom = Math.max(0, Math.min(1, (wind / Math.max(1, target) - 1) / 0.5));

    const s = 50 * utilScore + 40 * costScore + 10 * headroom;
    score = Math.round(Math.max(0, Math.min(100, s)));
  }

  return { pass, surviveWind: wind, checks, score, cost, paybackMonths: econ.paybackMonths };
}

// --- self-contained UI -----------------------------------------------------

const UI = {
  bg: "#12151c",
  panel: "#1b1f29",
  panelHover: "#232838",
  border: "#2c3242",
  text: "#e6e9ef",
  dim: "#8b93a7",
  accent: "#4da3ff",
  ok: "#43c07a",
  fail: "#e5544b",
  selected: "#2a3346",
};

/**
 * A dark-UI panel that lists the challenges (click to select + read the brief)
 * and shows the last evaluation's per-check pass/fail. Self-contained with
 * inline styles; append its element by passing the host container to the
 * constructor. Call `showResult` after evaluating, `dispose` to tear down.
 */
const CHALLENGE_DONE_KEY = "dfodgs.challenges.done";
function loadDoneChallenges(): Set<string> {
  try {
    return new Set<string>(JSON.parse(localStorage.getItem(CHALLENGE_DONE_KEY) || "[]"));
  } catch {
    return new Set<string>();
  }
}
function saveDoneChallenges(s: Set<string>): void {
  try {
    localStorage.setItem(CHALLENGE_DONE_KEY, JSON.stringify([...s]));
  } catch {
    /* storage disabled — completions just won't persist */
  }
}

export class ChallengePanel {
  private root: HTMLElement;
  private listEl: HTMLElement;
  private briefEl: HTMLElement;
  private resultEl: HTMLElement;
  private onSelect?: (ch: Challenge) => void;
  private itemEls = new Map<string, HTMLElement>();
  private selectedId: string | null = null;
  private completed = loadDoneChallenges();

  /** Colour every row by (selected, completed) — completed challenges go green. */
  private restyle(): void {
    for (const [id, el] of this.itemEls) {
      const sel = id === this.selectedId;
      const done = this.completed.has(id);
      el.style.background = sel ? UI.selected : UI.panel;
      el.style.borderColor = done ? UI.ok : sel ? UI.accent : UI.border;
      el.style.color = done ? UI.ok : UI.text;
    }
  }

  /** Mark a challenge as passed → persists + turns its row green. */
  private markCompleted(id: string): void {
    if (this.completed.has(id)) return;
    this.completed.add(id);
    saveDoneChallenges(this.completed);
    const el = this.itemEls.get(id);
    const ch = CHALLENGES.find((c) => c.id === id);
    if (el && ch) el.textContent = `✓ ${ch.name}`;
    this.restyle();
  }

  constructor(container: HTMLElement, opts: { onSelect?: (ch: Challenge) => void } = {}) {
    this.onSelect = opts.onSelect;

    const root = document.createElement("div");
    Object.assign(root.style, {
      fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      fontSize: "13px",
      color: UI.text,
      background: UI.bg,
      border: `1px solid ${UI.border}`,
      borderRadius: "8px",
      padding: "12px",
      width: "300px",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      gap: "10px",
    } as CSSStyleDeclaration);

    const title = document.createElement("div");
    title.textContent = "Design Challenges";
    Object.assign(title.style, {
      fontSize: "15px",
      fontWeight: "600",
      letterSpacing: "0.02em",
    } as CSSStyleDeclaration);
    root.appendChild(title);

    const list = document.createElement("div");
    Object.assign(list.style, {
      display: "flex",
      flexDirection: "column",
      gap: "6px",
    } as CSSStyleDeclaration);
    root.appendChild(list);
    this.listEl = list;

    const brief = document.createElement("div");
    Object.assign(brief.style, {
      color: UI.dim,
      lineHeight: "1.45",
      minHeight: "1em",
      borderTop: `1px solid ${UI.border}`,
      paddingTop: "10px",
    } as CSSStyleDeclaration);
    brief.textContent = "Select a challenge to see its brief.";
    root.appendChild(brief);
    this.briefEl = brief;

    const result = document.createElement("div");
    Object.assign(result.style, {
      display: "flex",
      flexDirection: "column",
      gap: "5px",
    } as CSSStyleDeclaration);
    root.appendChild(result);
    this.resultEl = result;

    for (const ch of CHALLENGES) this.addItem(ch);
    this.restyle();

    container.appendChild(root);
    this.root = root;
  }

  private addItem(ch: Challenge): void {
    const item = document.createElement("button");
    item.type = "button";
    item.textContent = this.completed.has(ch.id) ? `✓ ${ch.name}` : ch.name;
    Object.assign(item.style, {
      textAlign: "left",
      cursor: "pointer",
      color: UI.text,
      background: UI.panel,
      border: `1px solid ${UI.border}`,
      borderRadius: "6px",
      padding: "8px 10px",
      font: "inherit",
      transition: "background 0.12s",
    } as CSSStyleDeclaration);
    item.addEventListener("mouseenter", () => {
      if (this.selectedId !== ch.id) item.style.background = UI.panelHover;
    });
    item.addEventListener("mouseleave", () => {
      if (this.selectedId !== ch.id) item.style.background = UI.panel;
    });
    item.addEventListener("click", () => this.select(ch));
    this.listEl.appendChild(item);
    this.itemEls.set(ch.id, item);
  }

  private select(ch: Challenge): void {
    this.selectedId = ch.id;
    this.restyle();
    this.briefEl.textContent = ch.brief;
    this.resultEl.replaceChildren();
    this.onSelect?.(ch);
  }

  /** Render the result of the last evaluation for a challenge. */
  showResult(ch: Challenge, result: ChallengeResult): void {
    // ensure the challenge is visually selected + its brief shown
    this.selectedId = ch.id;
    this.briefEl.textContent = ch.brief;
    if (result.pass) this.markCompleted(ch.id); // turns the row green (persists)
    this.restyle();

    this.resultEl.replaceChildren();

    const header = document.createElement("div");
    const scoreTxt = result.score !== undefined ? `  ·  score ${result.score}` : "";
    header.textContent = `${result.pass ? "PASS" : "FAIL"}${scoreTxt}`;
    Object.assign(header.style, {
      fontWeight: "700",
      fontSize: "14px",
      color: result.pass ? UI.ok : UI.fail,
      borderTop: `1px solid ${UI.border}`,
      paddingTop: "10px",
    } as CSSStyleDeclaration);
    this.resultEl.appendChild(header);

    for (const c of result.checks) {
      const row = document.createElement("div");
      Object.assign(row.style, {
        display: "flex",
        alignItems: "baseline",
        gap: "8px",
      } as CSSStyleDeclaration);

      const mark = document.createElement("span");
      mark.textContent = c.ok ? "✓" : "✗";
      Object.assign(mark.style, {
        color: c.ok ? UI.ok : UI.fail,
        fontWeight: "700",
        width: "1em",
        flex: "0 0 auto",
      } as CSSStyleDeclaration);
      row.appendChild(mark);

      const label = document.createElement("span");
      label.textContent = c.label;
      Object.assign(label.style, { flex: "0 0 auto", color: UI.text } as CSSStyleDeclaration);
      row.appendChild(label);

      const detail = document.createElement("span");
      detail.textContent = c.detail;
      Object.assign(detail.style, {
        color: UI.dim,
        marginLeft: "auto",
        textAlign: "right",
      } as CSSStyleDeclaration);
      row.appendChild(detail);

      this.resultEl.appendChild(row);
    }
  }

  /** Remove the panel from the DOM. */
  dispose(): void {
    this.itemEls.clear();
    this.root.remove();
  }
}

/*
 * WIRING (maintainer — do this in the app; nothing else in this file needs it):
 *
 *   import { ChallengePanel, evaluateChallenge, type Challenge }
 *     from "@/game/challenges";
 *   import { compileTower } from "@/game/towerSpec";
 *
 *   let active: Challenge | null = null;
 *   const panel = new ChallengePanel(hostEl, {
 *     onSelect: (ch) => { active = ch; },
 *   });
 *
 *   // when the user hits "Test":
 *   function onTest(currentSpec, groundFn) {
 *     if (!active) return;
 *     const built = compileTower(currentSpec, groundFn); // BuiltTower
 *     const result = evaluateChallenge(active, built);   // pure + deterministic
 *     panel.showResult(active, result);
 *   }
 *
 * evaluateChallenge is pure (takes a BuiltTower) and self-contained: it runs the
 * solver headless internally, so the app only needs to compile the tower and
 * pass it in. Call panel.dispose() when tearing the UI down.
 */
